import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen, Clock, Search, Sparkles, Calendar } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { getBlogPosts } from '../services/api';
import { useDocumentMeta } from '../utils/useDocumentMeta';

const SITE_NAME = 'Orosz Coaching';

const formatDateHu = (d) => {
  if (!d) return '';
  try {
    const dt = new Date(d);
    return new Intl.DateTimeFormat('hu-HU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(dt);
  } catch {
    return '';
  }
};

const pickSummary = (post) =>
  (post?.summary || post?.excerpt || '').toString();

const KnowledgeBasePage = () => {
  const [posts, setPosts] = useState([]);
  const [status, setStatus] = useState('loading'); // loading | ok | error
  const [query, setQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');

  useDocumentMeta({
    title: `Tudástár — Edzés- és életmód cikkek | ${SITE_NAME}`,
    description:
      'Olvass szakértő cikkeket erőedzésről, hipertrófiáról, esztétikai testarányokról és életmódváltásról. Tudományos alapokon, gyakorlati nyelven.',
    canonical: `${typeof window !== 'undefined' ? window.location.origin : ''}/tudastar`,
    type: 'website',
  });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { posts: list } = await getBlogPosts();
        if (!alive) return;
        // API returns posts in the admin-defined manual order
        setPosts(Array.isArray(list) ? list : []);
        setStatus('ok');
      } catch {
        if (!alive) return;
        setStatus('error');
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const categories = useMemo(() => {
    const set = new Set();
    posts.forEach((p) => {
      if (p?.category) set.add(p.category);
    });
    return ['all', ...Array.from(set).sort()];
  }, [posts]);

  // The featured (newest) post is only shown when no search/filter is active.
  const isUnfiltered = query.trim() === '' && activeCategory === 'all';
  const featured = isUnfiltered ? posts[0] : null;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const base = featured ? posts.slice(1) : posts;
    return base.filter((p) => {
      const matchCategory = activeCategory === 'all' || p.category === activeCategory;
      const haystack = `${p.title || ''} ${pickSummary(p)} ${p.category || ''}`.toLowerCase();
      const matchQuery = q === '' || haystack.includes(q);
      return matchCategory && matchQuery;
    });
  }, [posts, query, activeCategory, featured]);

  return (
    <div className="min-h-screen bg-gray-950 text-white" data-testid="knowledge-base-page">
      <Header />

      {/* Hero */}
      <section className="relative pt-32 pb-12 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-yellow-900/20 via-gray-950 to-gray-950 pointer-events-none" />
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              'radial-gradient(circle at 20% 50%, rgba(250,204,21,0.08), transparent 40%)',
          }}
        />
        <div className="container mx-auto px-4 relative">
          <div className="flex items-center gap-2 text-yellow-400 mb-4">
            <BookOpen className="w-5 h-5" />
            <span className="text-sm font-semibold uppercase tracking-wider">Tudástár</span>
          </div>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-extrabold mb-4 leading-[1.05]"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
            data-testid="kb-hero-heading"
          >
            Tanulj <span className="text-yellow-400">szakértő cikkekből</span>,
            <br className="hidden sm:block" />
            ne tippekből.
          </h1>
          <p className="text-base md:text-lg text-gray-300 max-w-2xl mb-10">
            Tudományosan megalapozott, gyakorlati nyelven megírt írások edzésről,
            táplálkozásról, esztétikai testarányokról és életmódról — egy helyen.
          </p>

          {/* Search + filter */}
          <div className="flex flex-col gap-4">
            <div className="relative max-w-xl">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500 pointer-events-none" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Keresés a cikkek között…"
                data-testid="kb-search-input"
                className="w-full pl-12 pr-4 py-3 bg-gray-900/80 border border-gray-800 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-yellow-400 transition-colors"
              />
            </div>

            {categories.length > 1 && (
              <div className="flex flex-wrap gap-2" data-testid="kb-category-filter">
                {categories.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setActiveCategory(cat)}
                    data-testid={`kb-cat-${cat}`}
                    className={`px-4 py-1.5 text-sm font-medium rounded-full border transition-colors ${
                      activeCategory === cat
                        ? 'bg-yellow-400 text-gray-950 border-yellow-400'
                        : 'bg-gray-900 text-gray-300 border-gray-800 hover:border-yellow-400/40'
                    }`}
                  >
                    {cat === 'all' ? 'Minden' : cat}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Featured (latest) post */}
      {status === 'ok' && featured && (
        <section className="pb-12" data-testid="kb-featured-section">
          <div className="container mx-auto px-4">
            <div className="flex items-center gap-2 mb-5">
              <Sparkles className="w-4 h-4 text-yellow-400" />
              <span className="text-xs font-bold tracking-[0.2em] text-yellow-400 uppercase">
                Kiemelt cikk
              </span>
              <span className="flex-1 h-px bg-gradient-to-r from-yellow-400/40 to-transparent" />
            </div>

            <Link
              to={`/tudastar/${featured.slug}`}
              data-testid="kb-featured-card"
              className="group relative grid grid-cols-1 lg:grid-cols-5 gap-0 overflow-hidden rounded-2xl border border-gray-800 bg-gradient-to-br from-gray-900 to-gray-950 hover:border-yellow-400/50 transition-all duration-500 shadow-[0_0_60px_-15px_rgba(250,204,21,0.15)]"
            >
              {/* Image */}
              <div className="lg:col-span-3 relative overflow-hidden aspect-[16/10] lg:aspect-auto">
                {featured.image ? (
                  <img
                    src={featured.image}
                    alt={featured.title}
                    loading="eager"
                    className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700 ease-out"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-yellow-900/30 to-gray-800" />
                )}
                {/* Subtle overlay for legibility on mobile (image is below text there) */}
                <div className="absolute inset-0 bg-gradient-to-t lg:bg-gradient-to-r from-gray-950/80 lg:from-transparent via-transparent to-transparent pointer-events-none" />
                {/* Category pin on image */}
                {featured.category && (
                  <div className="absolute top-5 left-5 px-3 py-1.5 bg-yellow-400 text-gray-950 text-xs font-bold uppercase tracking-wider rounded-full shadow-lg">
                    {featured.category}
                  </div>
                )}
              </div>

              {/* Text */}
              <div className="lg:col-span-2 p-7 lg:p-10 flex flex-col justify-center">
                <h2
                  className="text-2xl md:text-3xl lg:text-[1.875rem] font-extrabold text-white mb-4 leading-tight group-hover:text-yellow-50 transition-colors"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  {featured.title}
                </h2>
                <p className="text-gray-400 text-base leading-relaxed mb-6 line-clamp-3">
                  {pickSummary(featured)}
                </p>

                <div className="flex items-center gap-4 text-xs text-gray-500 mb-6">
                  {featured.date && (
                    <span className="flex items-center gap-1.5">
                      <Calendar className="w-3.5 h-3.5" />
                      {formatDateHu(featured.date)}
                    </span>
                  )}
                  {featured.read_time && (
                    <span className="flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5" />
                      {featured.read_time}
                    </span>
                  )}
                </div>

                <span className="inline-flex items-center gap-2 text-yellow-400 font-semibold group-hover:gap-3 transition-all">
                  Olvasd el a cikket
                  <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </Link>
          </div>
        </section>
      )}

      {/* Posts grid */}
      <section className="pb-24">
        <div className="container mx-auto px-4">
          {status === 'ok' && filtered.length > 0 && (
            <div className="flex items-center gap-2 mb-5">
              <BookOpen className="w-4 h-4 text-gray-500" />
              <span className="text-xs font-bold tracking-[0.2em] text-gray-500 uppercase">
                {isUnfiltered ? 'További cikkek' : 'Találatok'}
              </span>
              <span className="flex-1 h-px bg-gradient-to-r from-gray-800 to-transparent" />
            </div>
          )}

          {status === 'loading' && (
            <div className="text-gray-500 text-center py-12" data-testid="kb-loading">
              Cikkek betöltése…
            </div>
          )}

          {status === 'error' && (
            <div className="text-red-400 text-center py-12" data-testid="kb-error">
              Hiba történt a cikkek betöltésekor. Kérlek frissítsd az oldalt.
            </div>
          )}

          {status === 'ok' && filtered.length === 0 && !featured && (
            <div className="text-gray-500 text-center py-12" data-testid="kb-empty">
              Nincs találat a keresési feltételekre.
            </div>
          )}

          {status === 'ok' && filtered.length > 0 && (
            <div
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
              data-testid="kb-grid"
            >
              {filtered.map((post) => (
                <Link
                  key={post.slug}
                  to={`/tudastar/${post.slug}`}
                  data-testid={`kb-card-${post.slug}`}
                  className="group bg-gray-900/60 border border-gray-800 rounded-xl overflow-hidden hover:border-yellow-400/40 hover:bg-gray-900 transition-all flex flex-col"
                >
                  {/* Card image */}
                  {post.image && (
                    <div className="relative aspect-[16/10] overflow-hidden bg-gray-900">
                      <img
                        src={post.image}
                        alt={post.title}
                        loading="lazy"
                        className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500 ease-out"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-transparent to-transparent pointer-events-none" />
                    </div>
                  )}

                  {/* Card body */}
                  <div className="p-6 flex flex-col flex-grow">
                    {post.category && (
                      <div className="text-yellow-400 text-xs font-bold uppercase tracking-wider mb-3">
                        {post.category}
                      </div>
                    )}
                    <h2 className="text-xl font-bold text-white mb-3 group-hover:text-yellow-50 leading-snug">
                      {post.title}
                    </h2>
                    <p className="text-gray-400 text-sm leading-relaxed mb-5 flex-grow line-clamp-3">
                      {pickSummary(post)}
                    </p>
                    <div className="flex items-center justify-between text-xs text-gray-500 pt-4 border-t border-gray-800">
                      <div className="flex items-center gap-3">
                        {post.date && (
                          <span className="flex items-center gap-1.5">
                            <Calendar className="w-3.5 h-3.5" />
                            {formatDateHu(post.date)}
                          </span>
                        )}
                        {post.read_time && (
                          <span className="flex items-center gap-1.5">
                            <Clock className="w-3.5 h-3.5" />
                            {post.read_time}
                          </span>
                        )}
                      </div>
                      <span className="flex items-center gap-1 text-yellow-400 group-hover:gap-2 transition-all">
                        Olvasd <ArrowRight className="w-3.5 h-3.5" />
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {status === 'ok' && filtered.length > 0 && (
            <p
              className="text-center text-sm text-gray-500 mt-10"
              data-testid="kb-count"
            >
              {filtered.length + (featured ? 1 : 0)} cikk jelenleg{' '}
              {activeCategory !== 'all' ? `a(z) "${activeCategory}" kategóriában` : 'a Tudástárban'}
            </p>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default KnowledgeBasePage;
